import axios from "axios";
import fetchIntercept from "fetch-intercept";
import { AuthService } from "../components/AuthProvider/authService";
import { signOut } from "@aws-amplify/auth";

let isRefreshing = false;
let refreshQueue = [];

function processQueue(token) {
  refreshQueue.forEach((cb) => cb(token));
  refreshQueue = [];
}

const getCommonHeaders = () => {
  const user = JSON.parse(localStorage.getItem("USER_AUTH") || "{}");
  const headers = {};
  const region = process.env.REACT_APP_REGION;

  if (region) {
    headers.region = region;
  }

  if (user.preferences?.selectedClient) {
    headers.client_id = user.preferences.selectedClient.id;
    headers.organization_id = user.preferences.selectedClient.organization.id;
    headers.organization_short_code =
      user.preferences.selectedClient.organization.short_code;
    headers.client_short_code = user.preferences.selectedClient.short_code;
    if (!user.internalUser) {
      headers.country_code = user.country_code;
    }
    headers.role_id = user.deRole || user.role;
  }

  return headers;
};

const getAuthToken = () => {
  const clientId = process.env.REACT_APP_USER_POOL_CLIENT_ID;
  const userKey = localStorage.getItem(
    `CognitoIdentityServiceProvider.${clientId}.LastAuthUser`,
  );
  if (!userKey) return null;

  return localStorage.getItem(
    `CognitoIdentityServiceProvider.${clientId}.${userKey}.idToken`,
  );
};

// 🔑 FIXED: Ensure fresh token before API call
const ensureFreshToken = async () => {
  // ✅ NEW: Check if token is already expired
  if (AuthService.isTokenExpired()) {
    console.log("⚠️ Token already expired, refreshing immediately...");

    if (isRefreshing) {
      return new Promise((resolve) => {
        refreshQueue.push(resolve);
      });
    }

    isRefreshing = true;

    try {
      const newToken = await AuthService.refreshToken();
      if (!newToken) throw new Error("Refresh returned null token");

      isRefreshing = false;
      processQueue(newToken);
      return newToken;
    } catch (error) {
      isRefreshing = false;
      processQueue(null);
      console.error("❌ Refresh failed, logging out");
      await signOut();
      window.location.replace("/login");
      throw error;
    }
  }

  // Check if token needs refresh (< 5 min remaining)
  const needsRefresh = AuthService.shouldRefreshToken();

  if (!needsRefresh) {
    // Token is still valid for >5 minutes, use existing
    return getAuthToken();
  }

  // Token expiring soon, refresh proactively
  console.log("⚠️ Token expiring in <5 min, refreshing before API call...");

  if (isRefreshing) {
    return new Promise((resolve) => {
      refreshQueue.push(resolve);
    });
  }

  isRefreshing = true;

  try {
    const newToken = await AuthService.refreshToken();
    if (!newToken) throw new Error("Refresh returned null token");

    isRefreshing = false;
    processQueue(newToken);
    return newToken;
  } catch (error) {
    isRefreshing = false;
    processQueue(null);
    console.error("❌ Proactive refresh failed:", error.message);
    return getAuthToken(); // Try with existing token
  }
};

export const registerFetchInterceptor = () => {
  fetchIntercept.register({
    request: async (url, config) => {
      const withDefaults = { ...config };
      const identityServiceDomain =
        process.env.REACT_APP_CLIENT_IDENTITY_SERVICE_DOMAIN;
      const svcApisDomain = process.env.REACT_APP_SVC_APIS_DOMAIN;

      if (
        url &&
        (url.includes(identityServiceDomain) || url.includes(svcApisDomain))
      ) {
        url = url.replace(/\/$/, "").replace("/?", "?");
      }

      if (
        url.toLowerCase().includes("api") &&
        !url.toLowerCase().includes("login")
      ) {
        if (withDefaults._isRetry) {
          return [url, withDefaults];
        }

        // 🔑 CRITICAL: Get fresh token (will refresh if needed)
        const token = await ensureFreshToken();

        const headers = getCommonHeaders();
        if (token) {
          headers.authz_token = token;
        }

        const newHeaders = new Headers(withDefaults.headers || {});
        Object.entries(headers).forEach(([k, v]) => newHeaders.set(k, v));
        withDefaults.headers = newHeaders;
      }

      return [url, withDefaults];
    },

    response: async (response) => {
      if (
        response.status === 401 &&
        (response.url.includes("/insights") ||
          response.url.includes("/ai/chat/sync"))
      ) {
        return response;
      }

      if (response.status === 401 && !response._retried) {
        response._retried = true;

        if (isRefreshing) {
          return new Promise((resolve) => {
            refreshQueue.push(async (token) => {
              if (!token) {
                await signOut();
                window.location.replace("/login");
                return resolve(response);
              }

              const headers = new Headers(response.request?.headers || {});
              headers.set("authz_token", token);

              const retry = await fetch(response.url, {
                method: response.request?.method || "GET",
                headers,
                body: response.request?.body,
                credentials: response.request?.credentials,
                _isRetry: true,
              });
              resolve(retry);
            });
          });
        }

        isRefreshing = true;

        try {
          console.log("⚠️ 401 received, refreshing token...");
          const newToken = await AuthService.refreshToken();

          if (!newToken) throw new Error("No token");

          isRefreshing = false;
          processQueue(newToken);

          const headers = new Headers(response.request?.headers || {});
          headers.set("authz_token", newToken);

          return fetch(response.url, {
            method: response.request?.method || "GET",
            headers,
            body: response.request?.body,
            credentials: response.request?.credentials,
            _isRetry: true,
          });
        } catch (error) {
          isRefreshing = false;
          processQueue(null);
          console.error("❌ Refresh failed, logging out");
          await signOut();
          window.location.replace("/login");
          return response;
        }
      }

      // Allow chat API to handle 403 permission errors without redirect
      if (response.status === 403 && response.url.includes("/ai/chat/sync")) {
        return response;
      }

      if (response.status === 403) {
        window.location.replace("/access-denied");
      }

      return response;
    },
  });
};

export const registerAxiosRequestInterceptor = () => {
  axios.interceptors.request.use(async (config) => {
    const identityServiceDomain =
      process.env.REACT_APP_CLIENT_IDENTITY_SERVICE_DOMAIN;
    const svcApisDomain = process.env.REACT_APP_SVC_APIS_DOMAIN;

    if (
      config.url &&
      (config.url.includes(identityServiceDomain) ||
        config.url.includes(svcApisDomain))
    ) {
      config.url = config.url.replace(/\/$/, "").replace("/?", "?");
    }

    if (
      config.url?.toLowerCase().includes("api") &&
      !config.url?.toLowerCase().includes("login")
    ) {
      // 🔑 CRITICAL: Get fresh token (will refresh if needed)
      const token = await ensureFreshToken();

      config.headers = { ...config.headers, ...getCommonHeaders() };
      if (token) {
        config.headers.authz_token = token;
      }
    }

    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (
        error.response?.status === 401 &&
        (error.config?.url?.includes("/insights") ||
          error.config?.url?.includes("/ai/chat/sync"))
      ) {
        return Promise.reject(error);
      }

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            refreshQueue.push((token) => {
              if (!token) {
                return reject(error);
              }
              originalRequest.headers.authz_token = token;
              resolve(axios(originalRequest));
            });
          });
        }

        isRefreshing = true;

        try {
          console.log("⚠️ 401 received, refreshing token...");
          const newToken = await AuthService.refreshToken();

          if (!newToken) throw new Error("No token");

          isRefreshing = false;
          processQueue(newToken);

          originalRequest.headers.authz_token = newToken;
          return axios(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;
          processQueue(null);
          await signOut();
          window.location.replace("/login");
          return Promise.reject(refreshError);
        }
      }

      // Allow chat API to handle 403 permission errors without redirect
      if (
        error.response?.status === 403 &&
        error.config?.url?.includes("/ai/chat/sync")
      ) {
        return Promise.reject(error);
      }

      if (error.response?.status === 403) {
        window.location.replace("/access-denied");
      }

      return Promise.reject(error);
    },
  );
};

export const registerAxiosInterceptor = registerAxiosRequestInterceptor;
